import type { DragEvent, JSX } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { personalKbApi, type PersonalFile, PersonalKbError } from "../lib/personalKbApi";
import { collectDroppedFiles, isSupportedUpload } from "../lib/collect-files";
import { FeishuConnectionSection } from "./FeishuConnectionSection";
import "../styles/personal-kb.css";

/**
 * 我的资料 connector menu (personal-materials.md, v3 AI-native). Opened from the
 * composer's + button. It is the ENTRY, not a file manager: two things (上传 /
 * 连接飞书) + the most recent item + 查看全部 into the full library view. A long
 * list never lives here. Upload accepts loose files (click) OR a whole folder
 * (drag-drop, recursed) — the system expands folders, the user never chooses
 * "file vs folder". Unsupported types in a folder are skipped. Copy is plain;
 * no sync/category jargon surfaces.
 */

/** Image types that go to the this-turn vision path (not the durable KB). */
const IMAGE_RE = /^image\/(png|jpe?g|gif|webp)$/i;
function isImage(f: File): boolean {
  if (IMAGE_RE.test(f.type)) return true;
  const i = f.name.lastIndexOf(".");
  return i >= 0 && ["png", "jpg", "jpeg", "gif", "webp"].includes(f.name.slice(i + 1).toLowerCase());
}

export function ResourceMenu({
  onClose,
  onOpenLibrary,
  onAddImages
}: {
  onClose: () => void;
  /** 查看全部 → open the full library manage view. */
  onOpenLibrary: () => void;
  /** Stage images as this-turn attachments (merged 📎). Absent → images rejected. */
  onAddImages?: (files: File[]) => void;
}): JSX.Element {
  const [files, setFiles] = useState<PersonalFile[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    setFiles(await personalKbApi.list());
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Dismiss on outside click / Escape.
  useEffect(() => {
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  /** Upload a batch (files or a folder's contents). Routes by type — the system
   *  decides, the user never picks a category: images become this-turn
   *  attachments (merged 📎), text docs land in the durable library, the rest
   *  are skipped. */
  const uploadMany = useCallback(
    async (all: File[]) => {
      const images = all.filter(isImage);
      const docs = all.filter((f) => isSupportedUpload(f.name));
      const skipped = all.length - images.length - docs.length;

      setErr("");
      setNote("");
      // Images → this-turn vision attachments (shown as composer thumbnails).
      if (images.length > 0 && onAddImages !== undefined) {
        onAddImages(images);
      }
      if (docs.length === 0) {
        // Only images (or nothing) — surface any skips, then close so the
        // composer thumbnails are visible.
        setErr(skipped > 0 ? "有文件类型暂不支持，已跳过。" : "");
        if (images.length > 0) onClose();
        return;
      }

      setBusy(true);
      try {
        setProgress(docs.length > 1 ? `上传 ${docs.length} 份…` : "上传中…");
        // One batched request → one embed round-trip + one BM25 rebuild.
        await personalKbApi.uploadMany(docs);
        await refresh();
        // Clear success: uploaded files are used automatically from now on.
        const extra: string[] = [];
        if (images.length > 0) extra.push(`${images.length} 张图片随本轮`);
        if (skipped > 0) extra.push(`跳过 ${skipped} 个不支持`);
        setNote(`已添加 ${docs.length} 份，答题时自动用${extra.length > 0 ? "（" + extra.join("，") + "）" : ""}`);
      } catch (e2) {
        setErr(e2 instanceof PersonalKbError ? e2.message : "上传没成功，稍后再试。");
      } finally {
        setBusy(false);
        setProgress("");
      }
    },
    [refresh, onAddImages, onClose]
  );

  async function onPick(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (picked.length > 0) await uploadMany(picked);
  }

  async function onDrop(e: DragEvent<HTMLDivElement>): Promise<void> {
    e.preventDefault();
    setDragOver(false);
    const dropped = await collectDroppedFiles(e.dataTransfer);
    if (dropped.length > 0) await uploadMany(dropped);
  }

  const recent = files[0];

  return (
    <div
      className={`rm-pop${dragOver ? " rm-drag" : ""}`}
      ref={rootRef}
      role="dialog"
      aria-label="我的资料"
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => void onDrop(e)}
    >
      <div className="rm-sec">
        我的资料 <span className="rm-sec-meta">传了就自动用 · 只有你</span>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept=".md,.txt,.markdown,.docx,.html,.htm,.pdf,image/png,image/jpeg,image/gif,image/webp"
        multiple
        className="pkb-file-input"
        onChange={(e) => void onPick(e)}
      />
      <button className="rm-row" type="button" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="rm-upload">
        <span className="rm-logo up">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
        </span>
        <span className="rm-meta">
          <span className="rm-name">{busy ? progress || "上传中…" : "上传"}</span>
          <span className="rm-sub">文件、图片或整个文件夹，拖进来也行</span>
        </span>
      </button>

      <FeishuConnectionSection variant="row" onPicked={() => void refresh()} />

      {note !== "" && (
        <div className="rm-note-ok" data-testid="rm-upload-ok">
          ✓ {note}
        </div>
      )}
      {err !== "" && (
        <div className="pkb-err" role="alert">
          {err}
        </div>
      )}

      {files.length > 0 && (
        <>
          <div className="rm-divider" />
          <div className="rm-sec">
            已添加 <span className="rm-sec-meta">{files.length} 份</span>
          </div>
          {recent !== undefined && (
            <div className="rm-row rm-file">
              <span className="rm-logo doc">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>
              </span>
              <span className="rm-meta">
                <span className="rm-name">{recent.title}</span>
                <span className="rm-sub">{recent.filename} · 答题自动用</span>
              </span>
            </div>
          )}
          <button
            className="rm-link"
            type="button"
            data-testid="rm-view-all"
            onClick={() => {
              onOpenLibrary();
              onClose();
            }}
          >
            查看全部 {files.length} 份 <span aria-hidden="true">→</span>
          </button>
        </>
      )}
    </div>
  );
}
